/**
 * Utility functions for parsing and formatting API validation errors.
 *
 * The backend returns validation errors in this format:
 * {
 *   "code": "ValidationFailed",
 *   "message": "[{\"Code\":\"NotEmptyValidator\",\"Message\":\"Field is required\",\"Field\":\"FieldName\"}]"
 * }
 *
 * This utility parses the JSON-encoded message and formats it for user display.
 */

/**
 * Represents a single validation error from the API.
 */
export interface ValidationError {
  code: string;
  message: string;
  field?: string;
}

/**
 * Parses a JSON-encoded validation error message from the API.
 * Returns null if the message is not a valid validation error array.
 */
export function parseValidationErrors(
  message: string,
): ValidationError[] | null {
  try {
    const parsed = JSON.parse(message);
    if (Array.isArray(parsed)) {
      return parsed.map(
        (error: { Code?: string; Message?: string; Field?: string }) => ({
          code: error.Code ?? "",
          message: error.Message ?? "",
          field: error.Field,
        }),
      );
    }
  } catch {
    // Not a JSON-encoded validation error array
  }
  return null;
}

/**
 * Formats validation errors into a user-friendly message.
 * - Single error: Returns just the error message
 * - Multiple errors: Returns messages joined with bullet separators
 */
export function formatValidationErrors(errors: ValidationError[]): string {
  if (errors.length === 0) {
    return "Validation failed";
  }

  if (errors.length === 1) {
    return errors[0].message;
  }

  return errors.map((e) => `\u2022 ${e.message}`).join("\n");
}

/**
 * Checks if an error code indicates a validation failure.
 */
export function isValidationError(code: string | undefined): boolean {
  return code === "ValidationFailed";
}

/**
 * Extracts a human-readable rule label from a FluentValidation field path.
 * e.g. "TransactionRules[salesLedger].Value[0].Label" → "Rule 1"
 * Returns null if the field doesn't match the expected pattern.
 */
export function parseRuleIndex(field: string | undefined): number | null {
  if (!field) return null;
  const match = field.match(/\.Value\[(\d+)\]/);
  return match ? Number(match[1]) : null;
}

/**
 * Checks whether all validation errors relate to transaction rules.
 */
export function areRuleValidationErrors(errors: ValidationError[]): boolean {
  return (
    errors.length > 0 &&
    errors.every((e) => e.field?.startsWith("TransactionRules"))
  );
}
